// Barra de navegación superior (Rediseño mayor 2026-09-01, pedido
// explícito del usuario a partir de una captura de referencia; destinos
// reales de las 5 pestañas completados en Fases 1-5, 2026-09-02). Vive en
// el Dashboard y en cada página del nav (Pacientes/Agenda/Historias/
// Reportes) — el resto de la app sigue con la barra lateral existente
// (PatientChartPage tiene su propio header), esto no es un layout global
// nuevo. El buscador de "Buscar paciente" de la esquina sigue
// deshabilitado a propósito — no se pidió conectarlo a la página
// Pacientes, y esta barra no le inventa un destino que nadie pidió.
import type { JSX } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { cerrarSesionHce } from '@/auth/cerrarSesion';
import { useContadorSala } from '@/lib/useContadorSala';

const TABS = ['dashboard', 'sala', 'pacientes', 'agenda', 'historias', 'reportes'] as const;
type Tab = (typeof TABS)[number];

const RUTA_DE: Record<Tab, string> = {
  dashboard: '/dashboard',
  sala: '/sala',
  pacientes: '/pacientes',
  agenda: '/agenda',
  historias: '/historias',
  reportes: '/reportes',
};

function iniciales(nombre: { given?: string[]; family?: string } | undefined): string {
  if (!nombre) return '?';
  const g = nombre.given?.[0]?.[0] ?? '';
  const f = nombre.family?.[0] ?? '';
  return (g + f).toUpperCase() || '?';
}

export function TopNav({ activo }: { activo: Tab }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const contador = useContadorSala();

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
      <div className="flex flex-wrap items-center gap-5">
        <span className="text-sm font-semibold text-hce-ink">{t('dashboard.topnav.marca')}</span>
        <nav className="flex flex-wrap gap-1 text-xs font-mono uppercase tracking-wide">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => tab !== activo && navigate(RUTA_DE[tab])}
              className={
                tab === activo
                  ? 'rounded-md bg-hce-pine/10 px-2.5 py-1.5 text-hce-pine'
                  : 'rounded-md px-2.5 py-1.5 text-hce-muted hover:text-hce-ink'
              }
            >
              {t(`dashboard.topnav.${tab}`)}
              {tab === 'sala' && contador.disponible && contador.esperando > 0 && (
                <>
                  {/* Contador de pacientes en espera, visible desde cualquier pantalla (lo mantiene AvisosSala). */}
                  <span className="ml-1.5 inline-grid min-w-5 place-items-center rounded-full bg-hce-clay px-1.5 font-mono text-[11px] normal-case text-white" aria-hidden="true">
                    {contador.esperando}
                  </span>
                  <span className="sr-only">{t('dashboard.topnav.salaEnEspera', { count: contador.esperando })}</span>
                </>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="text"
          disabled
          placeholder={t('dashboard.topnav.buscarPaciente')}
          aria-label={t('dashboard.topnav.buscarPaciente')}
          className="h-8 w-48 cursor-not-allowed rounded-md border border-border bg-hce-paper px-2.5 text-xs text-hce-muted placeholder:text-hce-muted"
        />
        <span className="grid size-8 shrink-0 place-content-center rounded-full bg-hce-pine/15 text-xs font-medium text-hce-pine">
          {iniciales(profile?.name?.[0])}
        </span>
        <button
          type="button"
          onClick={() => navigate('/mi-certificado')}
          className="text-xs text-hce-muted underline-offset-2 hover:text-hce-ink hover:underline"
        >
          {t('dashboard.topnav.miCertificado')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/seguridad')}
          className="text-xs text-hce-muted underline-offset-2 hover:text-hce-ink hover:underline"
        >
          {t('dashboard.topnav.seguridad')}
        </button>
        <Button variant="outline" size="sm" onClick={() => void cerrarSesionHce(medplum, navigate)}>
          {t('dashboard.topnav.cerrarSesion')}
        </Button>
      </div>
    </div>
  );
}
